import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { useCustomers, usePartsAndLocations } from "@/hooks/usePartsAndLocations";
import { apiFetch } from "@/utils/api";

const TRANSITIONS: Record<string, string[]> = {
  draft: ["submitted"],
  submitted: ["approved", "draft"],
  approved: ["picked", "submitted"],
  picked: ["shipped", "fulfilled", "approved"],
  shipped: ["fulfilled", "closed"],
  fulfilled: ["closed"],
  closed: [],
};

interface RequestRow {
  id: string;
  request_number: string;
  requestor: string;
  customer_name: string;
  status: string;
  needed_by_date: string;
  created_at: string;
}

export function EquipmentRequestPage() {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = searchParams.get("new") === "1";
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [detail, setDetail] = useState<{
    request: Record<string, unknown>;
    lines: Record<string, unknown>[];
    notes: Record<string, unknown>[];
    tracking: Record<string, unknown>[];
  } | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { parts, loading: partsLoading } = usePartsAndLocations();
  const { customers, loading: custLoading } = useCustomers();

  const loadDetail = async (id: string) => {
    const data = await apiFetch<typeof detail>(`/equipment-requests/${id}`);
    setDetail(data);
  };

  useEffect(() => {
    (async () => {
      try {
        if (requestId && !isNew) {
          await loadDetail(requestId);
        } else if (!isNew) {
          const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
          const data = await apiFetch<{ requests: RequestRow[] }>(`/equipment-requests${qs}`);
          setRequests(data.requests);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [requestId, isNew, statusFilter]);

  const transition = async (status: string) => {
    if (!requestId) return;
    try {
      await apiFetch(`/equipment-requests/${requestId}/transition`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      await loadDetail(requestId);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Transition failed");
    }
  };

  const addNote = async (note: string) => {
    if (!requestId || !note.trim()) return;
    await apiFetch(`/equipment-requests/${requestId}/notes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note }),
    });
    await loadDetail(requestId);
  };

  const addTracking = async (trackingNumber: string) => {
    if (!requestId || !trackingNumber.trim()) return;
    await apiFetch(`/equipment-requests/${requestId}/tracking`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ trackingNumber }),
    });
    await loadDetail(requestId);
  };

  if (loading || partsLoading || custLoading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (isNew) {
    return (
      <NewRequestForm
        parts={parts}
        customers={customers}
        onCancel={() => navigate("/equipment-request")}
        onCreated={(id) => navigate(`/equipment-request/${id}`)}
      />
    );
  }

  if (requestId && detail) {
    const r = detail.request;
    const nextStatuses = TRANSITIONS[String(r.status)] ?? [];

    return (
      <div className="space-y-4">
        <Link to="/equipment-request" className="text-sm text-[#1e3a4f] hover:underline">
          ← Back to Shipping
        </Link>
        <PageHeader
          title={String(r.request_number)}
          subtitle={`Requestor: ${String(r.requestor)}`}
          actions={
            nextStatuses.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {nextStatuses.map((s) => (
                  <Button key={s} size="sm" variant="secondary" onClick={() => transition(s)}>
                    Mark {s}
                  </Button>
                ))}
              </div>
            ) : undefined
          }
        />
        <div className="flex flex-wrap gap-4 text-sm">
          <StatusBadge status={String(r.status)} />
          {r.customer_name ? <span>Customer: {String(r.customer_name)}</span> : null}
          {r.needed_by_date ? <span>Needed by: {String(r.needed_by_date)}</span> : null}
        </div>
        {r.notes ? <p className="text-sm">{String(r.notes)}</p> : null}

        <DataTable
          columns={[
            { key: "part", header: "Part", render: (l) => String(l.part_name) },
            { key: "req", header: "Requested", className: "text-right", render: (l) => String(l.quantity_requested) },
            { key: "app", header: "Approved", className: "text-right", render: (l) => String(l.quantity_approved) },
            { key: "ful", header: "Fulfilled", className: "text-right", render: (l) => String(l.quantity_fulfilled ?? 0) },
          ]}
          rows={detail.lines}
          rowKey={(l) => String(l.id)}
        />

        <RequestNotesSection notes={detail.notes} onAdd={addNote} />
        <TrackingSection tracking={detail.tracking} onAdd={addTracking} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Shipping"
        actions={
          <Button asChild className="bg-[#1e3a4f] hover:bg-[#163041]">
            <Link to="/equipment-request?new=1">New Shipping</Link>
          </Button>
        }
      />
      <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="max-w-xs">
        <option value="">All statuses</option>
        {Object.keys(TRANSITIONS).map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </Select>
      <DataTable
        columns={[
          {
            key: "num",
            header: "Shipping #",
            render: (r) => (
              <Link to={`/equipment-request/${r.id}`} className="font-medium text-[#1e3a4f] hover:underline">
                {r.request_number}
              </Link>
            ),
          },
          { key: "requestor", header: "Requestor", render: (r) => r.requestor },
          { key: "customer", header: "Customer", render: (r) => r.customer_name || "—" },
          { key: "needed", header: "Needed By", render: (r) => r.needed_by_date || "—" },
          { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
        ]}
        rows={requests}
        rowKey={(r) => r.id}
      />
    </div>
  );
}

function NewRequestForm({
  parts,
  customers,
  onCancel,
  onCreated,
}: {
  parts: { id: string; part_name: string; part_number: string }[];
  customers: { id: string; name: string }[];
  onCancel: () => void;
  onCreated: (id: string) => void;
}) {
  const [requestor, setRequestor] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [neededByDate, setNeededByDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ partId: "", quantityRequested: 1, serialNotes: "", notes: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [options, setOptions] = useState<{
    requireCustomer: boolean;
    requireNeededByDate: boolean;
    requireSerialNotes: boolean;
  } | null>(null);

  useEffect(() => {
    apiFetch<{ options: NonNullable<typeof options> }>("/request-options")
      .then((d) => {
        setOptions(d.options);
      })
      .catch(() => {});
  }, []);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    if (options?.requireCustomer && !customerId) {
      setError("Customer is required.");
      setSubmitting(false);
      return;
    }
    if (options?.requireNeededByDate && !neededByDate) {
      setError("Needed-by date is required.");
      setSubmitting(false);
      return;
    }
    const validLines = lines.filter((l) => l.partId && l.quantityRequested > 0);
    if (options?.requireSerialNotes && validLines.some((l) => !l.serialNotes.trim())) {
      setError("Serial notes are required on all line items.");
      setSubmitting(false);
      return;
    }
    try {
      const data = await apiFetch<{ id: string }>("/equipment-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requestor,
          customerId: customerId || undefined,
          neededByDate: neededByDate || undefined,
          notes,
          lines: lines.filter((l) => l.partId && l.quantityRequested > 0),
        }),
      });
      onCreated(data.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create");
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="New Shipping" />
      <div className="bg-white rounded-lg shadow p-6 space-y-4 max-w-2xl">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Requestor</Label>
            <Input value={requestor} onChange={(e) => setRequestor(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Customer{options?.requireCustomer ? " *" : ""}</Label>
            <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)} required={options?.requireCustomer}>
              <option value="">None</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Needed By{options?.requireNeededByDate ? " *" : ""}</Label>
            <Input type="date" value={neededByDate} onChange={(e) => setNeededByDate(e.target.value)} required={options?.requireNeededByDate} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </div>
        <div className="space-y-2">
          <Label>Line Items</Label>
          {lines.map((line, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-3 items-end border-b pb-2">
              <Select value={line.partId} onChange={(e) => {
                const next = [...lines];
                next[i] = { ...next[i], partId: e.target.value };
                setLines(next);
              }}>
                <option value="">Select part…</option>
                {parts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.part_name}{p.part_number ? ` (${p.part_number})` : ""}
                  </option>
                ))}
              </Select>
              <Input
                type="number"
                min={1}
                value={line.quantityRequested}
                onChange={(e) => {
                  const next = [...lines];
                  next[i] = { ...next[i], quantityRequested: Number(e.target.value) };
                  setLines(next);
                }}
              />
              <Input
                placeholder={options?.requireSerialNotes ? "Serial notes *" : "Serial notes"}
                value={line.serialNotes}
                onChange={(e) => {
                  const next = [...lines];
                  next[i] = { ...next[i], serialNotes: e.target.value };
                  setLines(next);
                }}
              />
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setLines([...lines, { partId: "", quantityRequested: 1, serialNotes: "", notes: "" }])}
          >
            Add Line
          </Button>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#1e3a4f] hover:bg-[#163041]">
            {submitting ? "Saving…" : "Create Shipping"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RequestNotesSection({
  notes,
  onAdd,
}: {
  notes: Record<string, unknown>[];
  onAdd: (note: string) => Promise<void>;
}) {
  const [text, setText] = useState("");
  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-3">
      <h3 className="font-semibold text-[#1e3a4f]">Notes</h3>
      {notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No notes yet.</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {notes.map((n) => (
            <li key={String(n.id)} className="border-b pb-2">
              <span className="text-muted-foreground">{String(n.user_name)} · {String(n.created_at)}</span>
              <p>{String(n.note)}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input placeholder="Add a note…" value={text} onChange={(e) => setText(e.target.value)} />
        <Button
          size="sm"
          onClick={async () => {
            await onAdd(text);
            setText("");
          }}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function TrackingSection({
  tracking,
  onAdd,
}: {
  tracking: Record<string, unknown>[];
  onAdd: (num: string) => Promise<void>;
}) {
  const [num, setNum] = useState("");
  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-3">
      <h3 className="font-semibold text-[#1e3a4f]">Tracking</h3>
      {tracking.length > 0 && (
        <ul className="text-sm space-y-1">
          {tracking.map((t) => (
            <li key={String(t.id)}>{String(t.tracking_number)} · {String(t.created_at)}</li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input placeholder="Tracking number…" value={num} onChange={(e) => setNum(e.target.value)} />
        <Button size="sm" onClick={async () => { await onAdd(num); setNum(""); }}>Add</Button>
      </div>
    </div>
  );
}
